/**
 * INICIO — «¿Cómo estoy hoy?»
 *
 * El Home viejo abría con «Saldo disponible», que era la suma bruta de todos
 * los saldos: un número que no responde a nadie la pregunta que de verdad se
 * hace, que es *cuánto puedo gastar sin quedarme corto*. Su propio tooltip lo
 * admitía ("no incluye lo que debes").
 *
 * Este Home responde, en este orden:
 *   1. cuánto puedes usar          (liquidez − lo comprometido a 30 días)
 *   2. qué necesita tu atención    (accionable: cada tarjeta lleva a resolverla)
 *   3. qué viene                   (14 días, en lenguaje humano)
 *   4. cómo va el mes
 *   5. cuánto debes
 *   6. dónde está tu dinero
 *   7. cómo vas                    (el patrimonio, abajo y sin protagonismo)
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { track } from "@/lib/productTelemetry";
import { money, whenShort, whenLabel, daysUntil, humanErrorLine } from "@/lib/human";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Eye, EyeOff, AlertTriangle, ChevronRight, CreditCard, Repeat, Landmark, Plus,
} from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

const KIND_ICON = { card: CreditCard, debt: Landmark, recurring: Repeat };
const KIND_WORD = { card: "Tarjeta", debt: "Préstamo", recurring: "Pago frecuente" };

export default function HomePage() {
  useEffect(() => { track("home_viewed", { screen: "home" }); }, []);
  const { user } = useAuth();
  const nav = useNavigate();
  const [hidden, setHidden] = useState(false);
  const [home, setHome] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(async () => {
    try {
      const [h, a] = await Promise.all([
        api.get("/overview/home"),
        api.get("/assistant/today").catch(() => ({ data: { insights: [] } })),
      ]);
      setHome(h.data);
      setAlerts((a.data?.insights || []).filter(i => i.priority === "high").slice(0, 3));
    } catch (e) { toast.error(humanErrorLine(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // El diálogo de «anotar algo» lo monta Layout una sola vez; aquí sólo se
  // escucha cuándo se guardó algo para volver a pedir los números.
  useEffect(() => {
    window.addEventListener("td:data-changed", load);
    return () => window.removeEventListener("td:data-changed", load);
  }, [load]);

  const cur = home?.base_currency || "DOP";
  // El desglose por familias (misión 017). Lo calcula el backend con las mismas
  // autoridades que el resto del Inicio; aquí NO se vuelve a clasificar nada:
  // dos taxonomías, una en cada lado, acaban discrepando siempre.
  const tax = home?.taxonomy;
  // Lo pendiente ya NO se resta del dinero que hay (misión 016), así que
  // «Para usar» no puede salir negativo. Lo que sí puede pasar es que no
  // alcance para todo lo que viene, y eso se dice con su propio número.
  const pendiente = home?.can_use?.committed ?? 0;
  const vencido = home?.can_use?.overdue ?? 0;
  const cobertura = home?.can_use?.coverage;
  const noAlcanza = pendiente > (home?.can_use?.available ?? 0);
  // Sin dinero en ninguna cuenta y sin nada por pagar: es alguien que acaba de
  // entrar, no alguien que se quedó en cero.
  const isBrandNew = !!home
    && (home.accounts || []).every(a => Number(a.balance) === 0)
    && (home.upcoming || []).length === 0
    && Number(home.month?.income || 0) === 0
    && Number(home.month?.expense || 0) === 0;
  const hide = (node) => (hidden ? "••••••" : node);
  // Lo vencido NO se repite aquí: ya está arriba, en «Necesita tu atención».
  // Verlo dos veces hace pensar que son dos cosas distintas.
  const upcoming14 = (home?.upcoming || []).filter(i => {
    const d = daysUntil(i.due_date);
    return !i.overdue && d !== null && d <= 14;
  });

  return (
    <div className="space-y-6 pb-4" data-testid="home">
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[13px] text-slate-400">Hola, {user?.name?.split(" ")[0] || "amigo"}</p>
          <h1 className="text-2xl font-bold text-white mt-0.5">Tu dinero hoy</h1>
        </div>
        <div className="flex items-center gap-1.5">
          <ThemeToggle />
          <button onClick={() => setHidden(!hidden)} data-testid="toggle-balance-btn"
            aria-label={hidden ? "Mostrar cantidades" : "Ocultar cantidades"}
            className="p-2.5 rounded-xl bg-white/[0.04] border border-white/10 text-slate-300 hover:text-white min-h-11 min-w-11 flex items-center justify-center">
            {hidden ? <Eye className="w-[18px] h-[18px]" /> : <EyeOff className="w-[18px] h-[18px]" />}
          </button>
        </div>
      </header>

      {/* PRIMER USO — un tablero de ceros es honesto pero inútil: alguien que
          abre la app por primera vez necesita saber por dónde empezar, no
          contemplar RD$0 en siete sitios. */}
      {isBrandNew && (
        <section className="rounded-3xl bg-[#5b21b6] p-6" data-testid="first-run">
          <h2 className="text-xl font-bold text-white">Empecemos por lo básico</h2>
          <p className="mt-2 text-[15px] text-white/85 leading-relaxed">
            Dile a la app dónde guardas tu dinero —tu cuenta del banco, o el efectivo
            que cargas encima—. Con eso ya puede decirte cuánto puedes gastar.
          </p>
          <button onClick={() => nav("/mi-dinero/cuentas")} data-testid="first-run-cta"
            className="mt-5 w-full min-h-12 rounded-2xl bg-white text-[#15141b] font-bold">
            Agregar dónde guardo mi dinero
          </button>
        </section>
      )}

      {/* 1 — LO QUE PUEDES USAR */}
      {/* Un solo bloque de color en toda la pantalla, y sólido: el degradado
          competía con el número, que es lo único que aquí importa. */}
      {!isBrandNew && (
      <section className="rounded-3xl p-6 sm:p-7 bg-[#5b21b6]" data-testid="can-use">
        {/* TRES CIFRAS, NO UNA RESTA.
            Antes esto enseñaba `dinero − pendiente` y salía «Te falta 57,211»,
            como si el dinero no estuviera en el banco. Estaba: eran 5,007, y
            además había 62,218 por pagar. Las dos cosas son ciertas a la vez, y
            quién paga qué, cuándo y con qué dinero lo decide una persona, no
            una resta. */}
        <h2 className="text-[12px] uppercase tracking-[0.18em] text-white/80 font-bold">Para usar</h2>
        {/* UNA CIFRA POR DIVISA, sin convertir. Antes esto era un solo número en
            pesos que incluía los US$53 de RedotPay pasados a DOP: la app decía
            «RD$8,096» cuando en pesos había 5,007. Un dólar no paga la luz sin
            que alguien decida cambiarlo, y esa decisión no la toma una suma. */}
        {Object.entries(home?.can_use?.by_currency || { [cur]: home?.can_use?.available ?? 0 })
          .sort(([a], [b]) => (a === cur ? -1 : b === cur ? 1 : a.localeCompare(b)))
          .map(([divisa, monto], i) => (
            <p key={divisa} data-testid={`can-use-amount-${divisa}`}
              className={`font-bold text-white tabular-nums tracking-tight ${
                i === 0 ? "mt-2 text-4xl sm:text-5xl" : "mt-1 text-2xl text-white/90"}`}>
              {hide(money(monto, divisa))}
            </p>
          ))}
        <p className="mt-1 text-[13px] text-white/70">Lo que hay ahora en tus cuentas.</p>

        {home && pendiente > 0 && (
          <div className="mt-5 pt-4 border-t border-white/15 space-y-3">
            <div data-testid="home-pending">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[13px] text-white/80">Pendiente este mes</span>
                <span className="text-xl font-bold text-white tabular-nums">
                  {hide(money(home?.can_use?.committed_by_currency?.[cur] ?? pendiente, cur))}
                </span>
              </div>
              {Object.entries(home?.can_use?.committed_by_currency || {})
                .filter(([d]) => d !== cur)
                .map(([divisa, monto]) => (
                  <div key={divisa} className="flex items-baseline justify-between gap-3 mt-1">
                    <span className="text-[13px] text-white/60">en {divisa}</span>
                    <span className="text-[15px] font-semibold text-white/90 tabular-nums">
                      {hide(money(monto, divisa))}
                    </span>
                  </div>
                ))}
            </div>
            {vencido > 0 && (
              <div className="flex items-baseline justify-between gap-3" data-testid="home-overdue">
                <span className="text-[13px] text-amber-200">De eso, ya venció</span>
                <span className="text-xl font-bold text-amber-200 tabular-nums">
                  {hide(money(vencido, cur))}
                </span>
              </div>
            )}
            {/* Cobertura: una proyección, y se presenta como tal. Nunca como
                un saldo. */}
            {cobertura != null && noAlcanza && (
              <p className="text-[13px] text-white/70" data-testid="home-coverage">
                Con lo que hay cubres el {Math.round(cobertura * 100)}% de lo que viene.
              </p>
            )}
            {/* Que no alcance para todo lo que viene y a la vez existan
                inversiones no es una contradicción: son dos montones. Se dice
                para que nadie crea que la app se olvidó de su dinero, y NO se
                sugiere venderlas — esa decisión es suya. */}
            {noAlcanza && home?.investments?.count > 0 && (
              <p className="text-[13px] text-white/70" data-testid="can-use-investments-note">
                Tus inversiones van aparte y no se cuentan aquí.
              </p>
            )}
            <button onClick={() => nav("/por-pagar")} data-testid="can-use-cta"
              className="mt-1 w-full min-h-12 rounded-2xl bg-white text-[#15141b] font-bold">
              Ver qué tengo por pagar
            </button>
          </div>
        )}

        {/* Entró y gastaste viven aquí arriba a propósito: en un teléfono, la
            primera pantalla tiene que responder sola casi todo. El detalle del
            mes (comprometido, lo que queda) sigue más abajo. */}
        <dl className="mt-5 pt-4 border-t border-white/20 grid grid-cols-2 gap-4" data-testid="hero-month">
          <div>
            <dt className="text-[12px] text-white/70 font-medium">Entró este mes</dt>
            <dd className="text-[19px] font-bold text-white tabular-nums mt-0.5">
              {hide(money(home?.month?.income ?? 0, cur))}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] text-white/70 font-medium">Gastaste</dt>
            <dd className="text-[19px] font-bold text-white tabular-nums mt-0.5">
              {hide(money(home?.month?.expense ?? 0, cur))}
            </dd>
          </div>
        </dl>
      </section>
      )}

      {/* 2 — NECESITA TU ATENCIÓN */}
      {(home?.overdue_count > 0 || alerts.length > 0) && (
        <section data-testid="needs-attention">
          <SectionTitle icon={AlertTriangle} tone="text-amber-300">Necesita tu atención</SectionTitle>
          <div className="space-y-2">
            {(home?.upcoming || []).filter(i => i.overdue).slice(0, 3).map(i => (
              <button key={`${i.kind}-${i.id}`} onClick={() => nav("/por-pagar")}
                data-testid={`attention-${i.kind}-${i.id}`}
                className="w-full flex items-center gap-3 p-4 rounded-2xl bg-rose-500/10 border border-rose-500/25 text-left min-h-16 hover:bg-rose-500/15">
                <AlertTriangle className="w-5 h-5 text-rose-300 shrink-0" aria-hidden="true" />
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-semibold text-white truncate">{i.name}</span>
                  <span className="block text-[13px] text-rose-200">{whenShort(i.due_date)}</span>
                </span>
                <span className="text-[15px] font-bold text-white tabular-nums shrink-0">
                  {hide(money(i.amount, i.currency))}
                </span>
              </button>
            ))}
            {alerts.map((a, i) => (
              <div key={i} data-testid={`attention-alert-${i}`}
                className="flex items-center gap-3 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/25 min-h-16">
                <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-[15px] font-semibold text-white">{a.title}</span>
                  {a.detail && <span className="block text-[13px] text-amber-200/90">{a.detail}</span>}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 3 — LO PRÓXIMO */}
      {!isBrandNew && (
      <section data-testid="upcoming">
        <SectionTitle action={{ label: "Ver todo", to: "/por-pagar" }} nav={nav}>Lo próximo</SectionTitle>
        {upcoming14.length === 0 ? (
          <Empty>Nada más que pagar en las próximas dos semanas.</Empty>
        ) : (
          <ul className="space-y-2">
            {upcoming14.slice(0, 4).map(i => {
              const Icon = KIND_ICON[i.kind] || CreditCard;
              return (
                <li key={`${i.kind}-${i.id}`}>
                  <button onClick={() => nav("/por-pagar")} data-testid={`upcoming-${i.kind}-${i.id}`}
                    className="w-full flex items-center gap-3 p-3.5 rounded-2xl bg-white/[0.04] border border-white/10 text-left min-h-16 hover:bg-white/[0.07]">
                    <span className="w-10 h-10 rounded-xl bg-white/[0.06] flex items-center justify-center shrink-0">
                      <Icon className="w-[18px] h-[18px] text-slate-300" aria-hidden="true" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] font-semibold text-white truncate">{i.name}</span>
                      <span className="block text-[13px] text-slate-400">{whenLabel(i.due_date)}</span>
                    </span>
                    <span className="text-[15px] font-bold text-white tabular-nums shrink-0">
                      {hide(money(i.amount, i.currency))}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      )}

      {/* 4 — ESTE MES, POR FAMILIAS */}
      {/* «Gastaste 43,200» no dice nada por sí solo: no es lo mismo que se te
          fuera en el colegio que en salir. Cada familia lleva lo PAGADO (del
          libro) y lo PENDIENTE (de la expectativa), que son dos fuentes
          distintas a propósito — si salieran de la misma, Netflix contaría dos
          veces. */}
      {!isBrandNew && (
      <section className="rounded-2xl bg-white/[0.03] border border-white/10 p-5" data-testid="this-month">
        <h2 className="text-[15px] font-semibold text-white mb-4">Este mes</h2>
        <dl className="space-y-3">
          <Row label="Entró" value={hide(money(home?.month?.income ?? 0, cur))} tone="text-emerald-300" />
          <Row big label="Gastaste" value={hide(money(home?.month?.expense ?? 0, cur))} tone="text-rose-300" />
        </dl>

        <div className="mt-5 space-y-4">
          <FamilyBlock
            title="Compromisos" testid="family-commitments"
            hint="Doméstica, colegio, luz, préstamos."
            data={tax?.monthly_commitments} cur={cur} hide={hide} />
          <FamilyBlock
            title="Suscripciones" testid="family-subscriptions"
            hint="Lo que se renueva solo."
            data={tax?.subscriptions} cur={cur} hide={hide} />

          {/* Día a día no tiene «pendiente»: nadie compromete la gasolina del
              mes que viene. Sólo lo que ya se gastó, y cómo va contra el mes
              pasado. */}
          <div data-testid="family-daily">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-[14px] font-semibold text-white">Día a día</h3>
              <span className="text-[15px] font-semibold text-white tabular-nums">
                {hide(money(tax?.daily_variable?.total ?? 0, cur))}
              </span>
            </div>
            <p className="mt-1 text-[12px] text-slate-500">
              {money(tax?.daily_variable?.avg_per_day ?? 0, cur)} al día
              {(tax?.daily_variable?.prev_month_total ?? 0) > 0 && (
                <> · {(tax?.daily_variable?.delta_vs_prev ?? 0) >= 0 ? "+" : "−"}
                  {money(Math.abs(tax?.daily_variable?.delta_vs_prev ?? 0), cur)} vs. el mes pasado</>
              )}
            </p>
          </div>

          {(tax?.fees?.total ?? 0) > 0 && (
            <div data-testid="family-fees">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-[14px] font-semibold text-white">Comisiones e impuestos</h3>
                <span className="text-[15px] font-semibold text-white tabular-nums">
                  {hide(money(tax.fees.total, cur))}
                </span>
              </div>
              <p className="mt-1 text-[12px] text-slate-500">Lo que se llevaron el banco y el fisco.</p>
            </div>
          )}
        </div>

        <div className="mt-5 pt-4 border-t border-white/10">
          <Row big label="Te queda" value={hide(money(home?.month?.left ?? 0, cur))}
            tone={(home?.month?.left ?? 0) < 0 ? "text-rose-300" : "text-white"} />
          <p className="mt-1 text-[12px] text-slate-500">Lo que entró menos lo que salió de verdad.</p>
        </div>
      </section>
      )}

      {/* 4b — LA PROYECCIÓN, EN SU PROPIA CAJA */}
      {/* Va aparte y nunca dentro de «Para usar». Es un «si pagaras todo hoy»,
          puede salir negativo, y un número que puede ser negativo no es un
          saldo: mezclarlo con la caja es exactamente el error de la 016. */}
      {!isBrandNew && (tax?.projected_after_pending?.pending_subtracted ?? 0) > 0 && (
      <section className="rounded-2xl border border-white/10 p-5" data-testid="projection">
        <h2 className="text-[13px] uppercase tracking-[0.16em] text-slate-400 font-bold">Proyección</h2>
        <p className="mt-2 text-[14px] text-slate-300">Si pagaras todo lo pendiente:</p>
        <p className={`mt-1 text-3xl font-bold tabular-nums ${
          (tax.projected_after_pending.amount ?? 0) < 0 ? "text-rose-300" : "text-white"}`}
          data-testid="projection-amount">
          {hide(money(tax.projected_after_pending.amount ?? 0, cur))}
        </p>
        <p className="mt-2 text-[12px] text-slate-500">
          {/* En divisa nativa, igual que el titular: la cifra que se cita aquí
              tiene que ser la MISMA que se ve arriba, no su equivalente en
              pesos a la tasa de hoy. */}
          No es tu saldo. Tu dinero sigue siendo{" "}
          {hide(money(home?.can_use?.by_currency?.[cur] ?? 0, cur))}; esto es lo que
          quedaría después de pagar {money(tax.projected_after_pending.pending_subtracted, cur)}.
        </p>
      </section>
      )}

      {/* 4c — LO QUE VIENE MÁS ADELANTE (90 días) */}
      {/* Sólo aparece si hay cargos grandes en camino, y sólo trimestrales,
          semestrales y anuales: lo mensual ya se ve arriba. Es PREVISIÓN — no
          se resta de «Para usar», ni del pendiente del mes, ni de la
          proyección. Verlo venir y restarlo son cosas distintas: el seguro de
          diciembre no es un agujero de agosto, pero enterarse en diciembre
          tampoco vale. */}
      {(tax?.upcoming_90d?.items?.length ?? 0) > 0 && (
      <section data-testid="upcoming-90d">
        <SectionTitle>Próximos 90 días</SectionTitle>
        <ul className="space-y-2">
          {tax.upcoming_90d.items.slice(0, 5).map(i => (
            <li key={`${i.id}-${i.period}`}
              className="flex items-center gap-3 p-3.5 rounded-2xl bg-white/[0.03] border border-white/10 min-h-16"
              data-testid={`upcoming-90d-${i.id}`}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: i.color }} aria-hidden="true" />
              <span className="flex-1 min-w-0">
                <span className="block text-[15px] font-semibold text-white truncate">{i.name}</span>
                <span className="block text-[13px] text-slate-400">
                  {i.days_until === 0 ? "hoy" : `en ${i.days_until} días`}
                  {i.in_current_month && " · ya contado en este mes"}
                </span>
              </span>
              <span className="text-[15px] font-bold text-white tabular-nums shrink-0">
                {hide(money(i.amount, i.currency))}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[12px] text-slate-500">
          Cargos que no son de todos los meses. No están restados de «Para usar».
        </p>
      </section>
      )}

      {/* 5 — LO QUE DEBES */}
      {(home?.owe?.total ?? 0) > 0 && (
        <section className="rounded-2xl bg-white/[0.03] border border-white/10 p-5" data-testid="owe">
          <h2 className="text-[15px] font-semibold text-white">Debes</h2>
          <p className="mt-1 text-3xl font-bold text-white tabular-nums">{hide(money(home.owe.total, cur))}</p>
          <dl className="mt-4 space-y-2.5">
            <Row label="Tarjetas" value={hide(money(home.owe.cards, cur))} />
            <Row label="Préstamos" value={hide(money(home.owe.loans, cur))} />
          </dl>
        </section>
      )}

      {/* 6 — DÓNDE ESTÁ TU DINERO */}
      <section data-testid="where-money">
        <SectionTitle action={{ label: "Ver todo", to: "/mi-dinero" }} nav={nav}>Dónde está tu dinero</SectionTitle>
        {(home?.accounts || []).length === 0 ? (
          <Empty cta={{ label: "Agregar una cuenta", to: "/mi-dinero" }} nav={nav}>
            Todavía no has dicho dónde guardas tu dinero.
          </Empty>
        ) : (
          <ul className="space-y-2">
            {home.accounts.filter(a => a.purpose !== "INVESTMENT").slice(0, 4).map(a => (
              <li key={a.id}>
                <button onClick={() => nav("/mi-dinero")} data-testid={`home-account-${a.id}`}
                  className="w-full flex items-center gap-3 p-3.5 rounded-2xl bg-white/[0.04] border border-white/10 text-left min-h-14 hover:bg-white/[0.07]">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: a.color || "#8B5CF6" }} aria-hidden="true" />
                  <span className="flex-1 text-[15px] text-white truncate">{a.name}</span>
                  <span className="text-[15px] font-semibold text-white tabular-nums shrink-0">
                    {hide(money(a.balance, a.currency))}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 6b — INVERSIONES */}
      {home?.investments?.count > 0 && (
        <section data-testid="home-investments">
          <SectionTitle action={{ label: "Ver todo", to: "/mi-dinero" }} nav={nav}>Inversiones</SectionTitle>
          <div className="rounded-2xl border border-white/10 p-5">
            {/* En su divisa. Convertir el número grande a pesos con una tasa que
                cambia cada día inventa una precisión que no existe. */}
            <dl className="space-y-1.5">
              {Object.entries(home.investments.by_currency).map(([divisa, total]) => (
                <div key={divisa} className="flex items-baseline justify-between gap-3">
                  <dt className="text-[13px] text-slate-400">{divisa}</dt>
                  <dd className="text-xl font-bold text-white tabular-nums">
                    {hide(money(total, divisa))}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-[13px] text-slate-400">
              Esto no se cuenta en «Para usar».
            </p>
          </div>
        </section>
      )}

      {/* 7 — TU PROGRESO */}
      {home && !isBrandNew && (
        <section className="rounded-2xl border border-white/10 p-5" data-testid="net-worth">
          <h2 className="text-[13px] text-slate-400">Si vendieras todo y pagaras todo lo que debes</h2>
          <p className="mt-1 text-2xl font-bold text-white tabular-nums">
            ≈ {hide(money(home.net_worth, cur))}
          </p>
          {/* «≈» y la nota no son adorno: esta cifra convierte los dólares a la
              tasa de hoy, y mañana da otro número sin que nada haya cambiado. */}
          <p className="mt-1.5 text-[12px] text-slate-500">
            Incluye lo que tienes en dólares, convertido a la tasa de hoy.
          </p>
        </section>
      )}

    </div>
  );
}

/* -------------------------------------------------------------------------- */

function SectionTitle({ children, icon: Icon, tone, action, nav }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-[15px] font-semibold text-white flex items-center gap-2">
        {Icon && <Icon className={`w-4 h-4 ${tone}`} aria-hidden="true" />}
        {children}
      </h2>
      {action && (
        <button onClick={() => nav(action.to)} className="text-[13px] text-violet-300 hover:text-violet-200 flex items-center gap-0.5 min-h-11">
          {action.label} <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/** Una familia de gasto: lo pagado y lo pendiente, uno al lado del otro y
 *  nunca sumados. Sumarlos daría el doble de lo que se va a gastar. */
function FamilyBlock({ title, hint, data, cur, hide, testid }) {
  const pagado = data?.paid ?? 0;
  const pendiente = data?.pending ?? 0;
  const vencido = data?.overdue ?? 0;
  if (!pagado && !pendiente) return null;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[14px] font-semibold text-white">{title}</h3>
        {hint && <span className="text-[12px] text-slate-500 truncate">{hint}</span>}
      </div>
      <dl className="mt-2 space-y-1.5 pl-3 border-l border-white/10">
        <Row label="Pagados" value={hide(money(pagado, cur))} tone="text-slate-200" />
        <Row label="Pendientes" value={hide(money(pendiente, cur))} tone="text-amber-300" />
        {vencido > 0 && (
          <Row label="De eso, vencido" value={hide(money(vencido, cur))} tone="text-rose-300" />
        )}
      </dl>
    </div>
  );
}

function Row({ label, value, tone = "text-white", big = false }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className={`${big ? "text-[15px] font-semibold text-white" : "text-[14px] text-slate-400"}`}>{label}</dt>
      <dd className={`${big ? "text-xl" : "text-[15px]"} font-semibold tabular-nums ${tone}`}>{value}</dd>
    </div>
  );
}

function Empty({ children, cta, nav }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/15 p-6 text-center" data-testid="empty-state">
      <p className="text-[14px] text-slate-400 leading-relaxed">{children}</p>
      {cta && (
        <button onClick={() => nav(cta.to)}
          className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-semibold text-violet-300 hover:text-violet-200 min-h-11">
          <Plus className="w-4 h-4" aria-hidden="true" /> {cta.label}
        </button>
      )}
    </div>
  );
}
